import { formatUSD } from "@/lib/options";
import { Loader2, ScrollText } from "lucide-react";

export default function QuoteBreakdown({ quote, loading, error, metalLabel }) {
  return (
    <div
      className="glass-card p-7 sm:p-8"
      data-testid="quote-breakdown-panel"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Estimate</div>
          <h2 className="font-serif text-3xl text-charcoal-900 leading-none mt-2">
            Itemized Quote
          </h2>
        </div>
        <ScrollText
          className="text-champagne-500"
          size={28}
          strokeWidth={1.4}
        />
      </div>

      <div className="mt-6 divider-dotted" />

      {loading ? (
        <div className="py-10 flex items-center justify-center text-charcoal-500" data-testid="quote-loading">
          <Loader2 className="animate-spin mr-2" size={16} />
          <span className="text-sm">Calculating…</span>
        </div>
      ) : error ? (
        <div className="py-8 text-sm text-red-600" data-testid="quote-error">{error}</div>
      ) : !quote || !quote.line_items?.length ? (
        <div className="py-10 text-center" data-testid="quote-empty">
          <div className="font-serif italic text-lg text-charcoal-400">
            Awaiting your selections
          </div>
          <p className="text-sm text-charcoal-500 mt-2 max-w-[26ch] mx-auto">
            Choose a metal and at least one service to view a transparent estimate.
          </p>
        </div>
      ) : (
        <>
          <ul className="mt-6 space-y-5" data-testid="quote-line-items">
            {quote.line_items.map((it) => (
              <li
                key={it.id}
                className="flex items-start justify-between gap-4"
                data-testid={`line-${it.id}`}
              >
                <div className="min-w-0">
                  <div className="font-serif text-lg text-charcoal-900 leading-tight">
                    {it.label}
                  </div>
                  <div className="text-xs text-charcoal-500 mt-0.5">
                    {it.detail}
                  </div>
                  <div className="text-[11px] tracking-wider uppercase text-charcoal-400 mt-1">
                    Labor {formatUSD(it.labor)} · Materials {formatUSD(it.materials)}
                  </div>
                </div>
                <div
                  className="font-mono text-base text-charcoal-900 whitespace-nowrap"
                  data-testid={`line-${it.id}-subtotal`}
                >
                  {formatUSD(it.subtotal)}
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-7 divider-dotted" />

          <div className="mt-5 space-y-2 text-sm">
            <Row label="Labor" value={quote.labor_total} testid="row-labor" />
            <Row label="Materials" value={quote.materials_total} testid="row-materials" />
            <Row label="Bench fee" value={quote.shop_fee} testid="row-shop-fee" />
          </div>

          <div className="mt-6 flex items-end justify-between gap-4">
            <div>
              <div className="overline">Estimated Total</div>
              <div className="text-xs text-charcoal-400 mt-1">
                {metalLabel ? `${metalLabel} · ` : ""}quote valid 24h, subject to in-person assessment
              </div>
            </div>
            <div
              className="font-serif text-4xl text-charcoal-900 leading-none"
              data-testid="total-price-display"
            >
              {formatUSD(quote.total)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className="text-charcoal-500">{label}</span>
      <span className="font-mono text-charcoal-900">{formatUSD(value)}</span>
    </div>
  );
}
